import { nanoid } from 'nanoid';
import { useEffect, useRef, useState, useTransition } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from 'zustand/react';

import { fetchSectionDevices, saveSectionDevices } from '~modules/device/api';
import { getSection, removeSection, saveSection } from '~modules/section/api';
import { loadBySection } from '~modules/user/api/index.ts';
import { UserDto } from '~modules/user/model';
import { routerKeys } from '~shared/router';
import { toastError, toastSuccess } from '~shared/ui';

import { editStore } from './edit-store.ts';

export function useEditSection(sectionId: string) {
	const { section, devices, errors, setSection, setDevices, clearErrors, setError } = useStore(editStore);
	const [users, setUsers] = useState<UserDto[]>([]);
	const [pending, startLoading] = useTransition();
	const [saving, startSaveing] = useTransition();
	const isFirstRender = useRef(true);
	const navigate = useNavigate();

	useEffect(() => {
		if (isFirstRender.current) {
			isFirstRender.current = false;
			startLoading(async () => {
				setSection({});
				setDevices([]);
				clearErrors();
				if (sectionId !== 'add' && !isNaN(Number(sectionId))) {
					try {
						const section = await getSection(Number(sectionId));
						setSection(section);
						setDevices(
							(await fetchSectionDevices(Number(sectionId))).map(itm => ({ ...itm, key: nanoid() })),
						);
						const userlist = await loadBySection(Number(sectionId));
						setUsers(userlist);
					} catch (err) {
						console.error(err);
						toastError('Ошибка загрузки данных');
						navigate(routerKeys.page404());
					}
				}
			});
		}
	}, [clearErrors, sectionId, setDevices, setSection, navigate]);

	function addDevice() {
		setDevices([...devices, { key: nanoid() }]);
	}

	function deleteDevice(key: string) {
		setDevices(devices.filter(itm => itm.key !== key));
		setError(key, null);
	}

	function deviceChangeNumber(key: string, value: string) {
		setDevices(devices.map(itm => (itm.key === key ? { ...itm, number: value } : itm)));
		setError(key, null);
	}

	function deviceChangeCode(key: string, value: string) {
		setDevices(devices.map(itm => (itm.key === key ? { ...itm, code: value } : itm)));
		setError(key, null);
	}

	function changeTitle(title: string) {
		setSection({ ...section, title });
	}

	function changeLat(lat: string) {
		let value = section.lat;
		if (!isNaN(Number(lat))) {
			value = lat;
		}
		if (!value || value.trim().length <= 0) {
			value = null;
		}
		setSection({ ...section, lat: value });
	}

	function changeLon(lon: string) {
		let value = section.lon;
		if (!isNaN(Number(lon))) {
			value = lon;
		}
		if (!value || value.trim().length <= 0) {
			value = null;
		}
		setSection({ ...section, lon: value });
	}

	function changeRc(checked: boolean) {
		setSection({ ...section, is_rc: checked });
	}

	function changeAllAllowed(checked: boolean) {
		setError('users', null);
		setSection({ ...section, is_all_allowed: checked });
	}

	function addUser(user: UserDto) {
		setError('users', null);
		setUsers(old => [...old, user]);
	}

	function delUser(user_id: number) {
		setUsers(old => old.filter(itm => itm.id !== user_id));
	}

	function save() {
		clearErrors();
		let isError = false;
		if (!section.title || section.title.trim().length <= 0) {
			setError('title', 'Введите название участка');
			isError = true;
		}
		if (section.lat || section.lon) {
			if (!section.lat) {
				setError('lat', 'Введите широту участка');
				isError = true;
			}
			if (!section.lon) {
				setError('lon', 'Введите долготу участка');
				isError = true;
			}
		}
		for (const device of devices) {
			if (!device.number) {
				setError(`${device.key}_number`, 'Введите номер устройства');
				isError = true;
			}
			if (!device.code) {
				setError(`${device.key}_code`, 'Введите идентификатор устройства');
				isError = true;
			}
		}
		if (!section.is_all_allowed && users.length <= 0) {
			setError('users', 'Добавьте хотя бы одного пользователя');
			isError = true;
		}
		if (isError) {
			toastError('Проверьте правильность заполнения полей');
		} else {
			startSaveing(async () => {
				try {
					const saved = await saveSection(
						section,
						users.map(itm => itm.id!),
					);
					await saveSectionDevices(saved.id!, devices);
					toastSuccess('Данные успешно сохранены');
					navigate(routerKeys.cabinet.admin.sections.root());
				} catch (err) {
					console.error(err);
					toastError('Ошибка сохранения данных');
				}
			});
		}
	}

	function remove() {
		startSaveing(async () => {
			try {
				if (section.id) await removeSection(section.id);
				toastSuccess('Участок успешно удален');
				navigate(routerKeys.cabinet.admin.sections.root());
			} catch (err) {
				console.error(err);
				toastError('Ошибка удаления');
			}
		});
	}

	return {
		pending,
		section,
		devices,
		errors,
		addDevice,
		deleteDevice,
		deviceChangeNumber,
		deviceChangeCode,
		save,
		remove,
		changeTitle,
		changeLat,
		changeLon,
		changeRc,
		changeAllAllowed,
		saving,
		addUser,
		users,
		delUser,
	};
}
